import { useRef, useState } from 'react';
import { api, type Batch, type NameImport, type Role } from '../api';
import { Banner, Card, Empty, Loading, useAsync, when } from '../ui';

/**
 * Bulk upload.
 *
 * The file is validated as a whole before anything is created. If a single row
 * is malformed the batch is rejected outright and the operator gets a
 * per-row report they can download, fix, and re-upload — no half-ingested
 * payout file, ever.
 */
/**
 * The columns, in the order the template writes them. Kept beside the sample
 * rather than derived from it: an operator needs prose, and prose cannot be
 * generated from a header line.
 */
const COLUMN_GUIDE: { name: string; need: 'Always' | 'If bank' | 'If wallet' | 'Optional'; what: string }[] = [
  { name: 'referring_driver_id', need: 'Always', what: 'The driver who made the referral, as they are known in your system.' },
  { name: 'referred_driver_id', need: 'Always', what: 'The new driver. Must be different from the referrer.' },
  { name: 'phone', need: 'Always', what: 'The REFERRER\u2019s mobile \u2014 the driver being paid, not the new one. 08031234567 or +2348031234567.' },
  { name: 'commission_amount', need: 'Always', what: 'In kobo, whole numbers only. \u20a625,000 is 2500000.' },
  { name: 'payout_channel', need: 'Optional', what: 'bank or wallet \u2014 only when you already hold the driver\u2019s account. Leave every payout column blank for a driver who joined at the join link; we use what they gave us there.' },
  { name: 'bank_code', need: 'If bank', what: 'NIBSS code for the bank \u2014 058 is GTBank, 044 is Access.' },
  { name: 'account_number', need: 'If bank', what: 'Ten-digit NUBAN.' },
  { name: 'wallet_provider', need: 'If wallet', what: 'opay, palmpay, kuda, and so on.' },
  { name: 'wallet_id', need: 'If wallet', what: 'The wallet number or account handle.' },
  { name: 'referring_bvn', need: 'Optional', what: 'Eleven digits. Speeds the check up; we will ask the driver, or use their join-link details, if you leave it out.' },
  { name: 'referred_bvn', need: 'Optional', what: 'Eleven digits, same as above.' },
  { name: 'referred_phone', need: 'Optional', what: 'The new driver\u2019s own mobile. Helps confirm the referrer and the new driver aren\u2019t the same person.' },
  { name: 'device_id', need: 'Optional', what: 'The device the referral came from. Helps catch one person running many accounts.' },
  { name: 'external_ref', need: 'Optional', what: 'Your own reference. Comes back on the webhook, and stops the same row paying twice.' },
  { name: 'first_name', need: 'Optional', what: 'The referrer\u2019s first name, as on your sheet. Lets us check the name on their BVN without anyone typing it in.' },
  { name: 'last_name', need: 'Optional', what: 'The referrer\u2019s last name, as on your sheet.' },
];

export function Batches({ role }: { role: Role }) {
  const { data, error, loading, reload } = useAsync(() => api.batches(), []);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ accepted: boolean; batch_id: string; total_rows: number; errors: Batch['errors'] } | null>(null);
  const [clientId, setClientId] = useState('indrive_ng');
  const fileRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);

  // The sample shown on screen and the file the Download button produces are
  // the same bytes, fetched from the endpoint that serves the template. Keeping
  // a second copy in the console is how the two quietly drift apart.
  const template = useAsync(() => api.csvTemplate(), []);

  function copyTemplate() {
    if (!template.data) return;
    void navigator.clipboard.writeText(template.data).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }

  function downloadTemplate() {
    if (!template.data) return;
    const url = URL.createObjectURL(new Blob([template.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'indrive-referrals-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function upload(file: File) {
    setBusy(true);
    setResult(null);
    try {
      const csv = await file.text();
      const r = await api.upload(clientId, file.name, csv);
      setResult(r);
      reload();
    } catch (e) {
      setResult({ accepted: false, batch_id: '', total_rows: 0, errors: [{ row: 0, column: null, code: 'upload_failed', message: (e as Error).message }] });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function downloadErrors() {
    if (!result?.errors?.length) return;
    const csv = ['row,column,code,message', ...result.errors.map((e) => `${e.row},${e.column ?? ''},${e.code},"${e.message.replace(/"/g, '""')}"`)].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-errors-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="stack">
      {role === 'zeeh_admin' && <NamesImport />}
      <Card title="Upload referrals">
        <div className="field" style={{ marginBottom: 14, maxWidth: 260 }}>
          <label htmlFor="cid">Client</label>
          <input id="cid" type="text" value={clientId} onChange={(e) => setClientId(e.target.value)} />
        </div>

        <div
          className={`dropzone${over ? ' over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            const f = e.dataTransfer.files[0];
            if (f) void upload(f);
          }}
          onClick={() => fileRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
        >
          {busy ? (
            <>
              <div className="big"><span className="spin" /> Validating</div>
              <div className="small">Checking every row before anything is created.</div>
            </>
          ) : (
            <>
              <div className="big">Drop a CSV here, or click to choose</div>
              <div className="small">
                Four columns are required. The template below has all of them, filled in.
              </div>
            </>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
          />
        </div>

        {result && (
          <div style={{ marginTop: 16 }}>
            {result.accepted ? (
              <Banner tone="ok" title="Accepted">
                {result.total_rows} row{result.total_rows === 1 ? '' : 's'} queued for processing as batch{' '}
                <span className="mono">{result.batch_id.slice(0, 8)}</span>. KYC and fraud checks start immediately.
              </Banner>
            ) : (
              <Banner
                tone="danger"
                title="Rejected — nothing was created"
                action={result.errors?.length ? <button className="btn sm" onClick={downloadErrors}>Download report</button> : undefined}
              >
                {result.errors?.length ?? 0} row{result.errors?.length === 1 ? '' : 's'} failed validation.
                The whole file is validated before any referral is created, so no part of this upload was processed.
              </Banner>
            )}

            {!result.accepted && result.errors && result.errors.length > 0 && (
              <div className="table-wrap" style={{ marginTop: 12, maxHeight: 280, overflowY: 'auto', border: '1px solid var(--rule)', borderRadius: 'var(--r)' }}>
                <table>
                  <thead><tr><th className="num">Row</th><th>Column</th><th>Code</th><th>Problem</th></tr></thead>
                  <tbody>
                    {result.errors.slice(0, 100).map((e, i) => (
                      <tr key={i}>
                        <td className="num">{e.row || '—'}</td>
                        <td className="mono small">{e.column ?? '—'}</td>
                        <td><span className="code-chip">{e.code}</span></td>
                        <td className="small">{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card title="What the file should look like">
        <p className="small" style={{ marginTop: 0, marginBottom: 14, color: 'var(--ink-2)', lineHeight: 1.65 }}>
          Start from the template — it already has every column in the right order, and three example rows: one
          paid to a bank account, one to a wallet, and one for a driver who already joined at the join link, with
          no payout details at all. Delete the examples, paste your own rows in, upload.
        </p>

        <div className="toolbar" style={{ marginBottom: 14 }}>
          <button className="btn primary" onClick={downloadTemplate} disabled={!template.data}>
            Download template
          </button>
          <button className="btn" onClick={copyTemplate} disabled={!template.data}>
            {copied ? 'Copied' : 'Copy as text'}
          </button>
        </div>

        {template.loading && <Loading />}
        {template.error && <Banner tone="danger">Could not load the template — {template.error}</Banner>}
        {template.data && <pre className="csv-sample" aria-label="Example CSV">{template.data}</pre>}

        <div className="sep" />

        <div className="table-wrap">
          <table>
            <thead>
              <tr><th>Column</th><th style={{ width: 92 }}>Needed</th><th>What goes in it</th></tr>
            </thead>
            <tbody>
              {COLUMN_GUIDE.map((c) => (
                <tr key={c.name}>
                  <td className="mono small">{c.name}</td>
                  <td>
                    <span className={`need ${c.need === 'Always' ? 'req' : c.need === 'Optional' ? 'opt' : 'cond'}`}>
                      {c.need}
                    </span>
                  </td>
                  <td className="small">{c.what}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="sep" />

        <p className="small" style={{ margin: 0, color: 'var(--ink-3)', lineHeight: 1.65 }}>
          Two things catch people out. <b>Amounts are in kobo</b>, not naira — ₦25,000 is written{' '}
          <span className="mono">2500000</span>. And <b>a driver can only be referred once, ever</b>, so if the
          same referred_driver_id appears twice in one file the upload is rejected and tells you which rows clash.
        </p>
      </Card>

      <Card title="Recent batches" flush>
        {loading && <Loading />}
        {error && <div style={{ padding: 18 }}><Banner tone="danger">{error}</Banner></div>}
        {!loading && data && (data.batches.length === 0 ? (
          <Empty title="No batches yet" hint="Uploaded files appear here with their progress and error report." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>File</th><th>Status</th><th style={{ width: 160 }}>Progress</th>
                  <th className="num">Passed</th><th className="num">Failed</th><th className="num">Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {data.batches.map((b) => {
                  const pct = b.total_rows === 0 ? 0 : Math.round((b.processed / b.total_rows) * 100);
                  return (
                    <tr key={b.batch_id}>
                      <td>
                        <div>{b.source_file}</div>
                        <div className="mono small muted">{b.batch_id.slice(0, 8)}</div>
                      </td>
                      <td>
                        <span className={`pill ${b.status === 'completed' ? 'ok' : b.status === 'rejected' ? 'danger' : 'info'}`}>
                          {b.status}
                        </span>
                      </td>
                      <td>
                        <div className="progress"><i style={{ width: `${pct}%` }} /></div>
                        <div className="small muted" style={{ marginTop: 4 }}>{b.processed} / {b.total_rows}</div>
                      </td>
                      <td className="num">{b.passed}</td>
                      <td className="num" style={b.failed > 0 ? { color: 'var(--danger)' } : undefined}>{b.failed}</td>
                      <td className="num small muted">{when(b.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </Card>
    </div>
  );
}

/**
 * inDrive's names for drivers who were uploaded before names were kept.
 *
 * Takes inDrive's sheets exactly as they send them — the .xlsx files, or CSV —
 * several at once, and starts the moment they are chosen. Fills in the name
 * only for drivers who have none. Nothing about any referral changes; the
 * name is what the NIBSS check and the review queue compare against.
 */
function NamesImport() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [results, setResults] = useState<{ file: string; result?: NameImport; error?: string }[]>([]);

  async function run(files: File[]) {
    if (files.length === 0) return;
    setBusy(true);
    setResults([]);
    const out: { file: string; result?: NameImport; error?: string }[] = [];
    for (const f of files) {
      try {
        const isCsv = /\.csv$/i.test(f.name) || f.type === 'text/csv';
        const body = isCsv
          ? { file_name: f.name, csv: await f.text() }
          : { file_name: f.name, xlsx_base64: toBase64(new Uint8Array(await f.arrayBuffer())) };
        out.push({ file: f.name, result: await api.importIndriveNames(body) });
      } catch (e) {
        out.push({ file: f.name, error: (e as Error).message });
      }
      setResults([...out]);
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <Card title="Add inDrive's names to drivers already on file">
      <p className="small" style={{ marginTop: 0, lineHeight: 1.6 }}>
        Drop inDrive's qualifier sheets here exactly as they sent them (Excel or CSV), several at once. Each driver
        with no name on file gets the name from the sheet; anyone who already has one keeps it. No referral changes.
      </p>
      <div
        className={`dropzone${over ? ' over' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => fileRef.current?.click()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); void run([...e.dataTransfer.files]); }}
      >
        {busy ? <span className="spin" /> : <div className="big">Drop inDrive's sheets here, or click to choose</div>}
        <input
          ref={fileRef}
          type="file"
          multiple
          accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
          style={{ display: 'none' }}
          onChange={(e) => void run([...(e.target.files ?? [])])}
        />
      </div>
      {results.map(({ file, result, error }) => (
        <div key={file} style={{ marginTop: 12 }}>
          {error && <Banner tone="danger" title={file}>{error}</Banner>}
          {result && result.errors.length > 0 && (
            <Banner tone="danger" title={`${file}: nothing was added`}>
              {result.errors.slice(0, 6).map((e) => <div key={`${e.row}-${e.code}`}>{e.row ? `Row ${e.row}: ` : ''}{e.message}</div>)}
            </Banner>
          )}
          {result && result.errors.length === 0 && (
            <Banner tone="ok" title={file}>
              {result.named} named · {result.already_named} already had a name
              {result.no_name.length > 0 && <> · no name on the sheet: {result.no_name.join(', ')}</>}
              {result.not_found.length > 0 && <> · not on file: {result.not_found.join(', ')}</>}
            </Banner>
          )}
        </div>
      ))}
    </Card>
  );
}

function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
